// OpenTelemetry
import { CompositePropagator } from "@opentelemetry/core";
import { propagation, ContextManager } from "@opentelemetry/api";

// Otel Setup (Trace Provider, Processor, Exporter, ...)
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { ConsoleSpanExporter } from "@opentelemetry/sdk-trace-base";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { ZoneContextManager } from "@opentelemetry/context-zone";

// Plugins
import { Resource } from "@opentelemetry/resources";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { AWSXRayPropagator } from "@opentelemetry/propagator-aws-xray";
/**
 * Sets up OpenTelemetry propagator, collector, and exporter.
 * Registers Traces Instrumentation
 */
export function initTraces(): void {
  const propagator = new CompositePropagator({
    propagators: [new AWSXRayPropagator()],
  });

  propagation.setGlobalPropagator(propagator);

  // setup Collector
  const collectorOptions = {
    url: "https://otlp.telemetryhub.com/v1/traces",
    headers: {
      "content-type": "application/json",
    },
  };

  // setup Span Resource
  const resource = new Resource({
    [SemanticResourceAttributes.SERVICE_NAME]: "web",
    [SemanticResourceAttributes.SERVICE_NAMESPACE]: "telemetryhub",
    [SemanticResourceAttributes.SERVICE_INSTANCE_ID]: "web",
    [SemanticResourceAttributes.SERVICE_VERSION]:
      process.env.VERSION || "0.0.0",
    "telemetryhub.key":
      "60974a75-6ead-42d0-84a9-ceaf2047e38a:4676989b-24cf-4ed4-8ca2-eab5b7292f88:3066704",
  });

  // init provider and exporter
  const provider = new WebTracerProvider({ resource });
  const exporter = new OTLPTraceExporter(collectorOptions);
  // const exporter = new ConsoleSpanExporter();  // This can be used in Dev mode to see the traces in the console
  provider.register({
    contextManager: new ZoneContextManager().enable() as ContextManager,
    propagator,
  });

  // Add Span Processor to provider
  provider.addSpanProcessor(
    new BatchSpanProcessor(exporter, {
      // The maximum queue size. After the size is reached spans are dropped.
      maxQueueSize: 100,
      // The maximum batch size of every export. It must be smaller or equal to maxQueueSize.
      maxExportBatchSize: 50,
      // The interval between two consecutive exports
      scheduledDelayMillis: 500,
      // How long the export can run before it is cancelled
      exportTimeoutMillis: 30000,
    })
  );

  // Instrumentation
  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new UserInteractionInstrumentation(),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [/.*\.amazonaws.com/g],
        ignoreUrls: [/.*otlp\.telemetryhub\.com.*/],
      }),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [/.*\.amazonaws.com/g],
        ignoreUrls: [/.*otlp\.telemetryhub\.com.*/],
      }),
    ],
    tracerProvider: provider,
  });
}
